import { useEffect, useMemo, useState, useCallback, useRef, memo } from 'react'
import PhotoSwipe from 'photoswipe'
import PhotoSwipeUIDefault from 'photoswipe/dist/photoswipe-ui-default'
import { Badge } from '@leroy-merlin-br/backyard-react-ui'

import { GenericCarousel } from 'reactGarden/GenericCarousel'

import { ZOOM_OPTIONS, TRACKING_EVENTS } from './constants'
import { Navigation } from './components/Navigation'
import { YoutubePlayer } from './components/YoutubePlayer'
import { SketchfabModel } from './components/SketchfabModel'
import * as S from './styled'

const ImagesCarousel = ({
  items,
  carouselTrackingValue,
  sketchfabId,
  sketchfabThumb,
  sketchfabPlaceholder
}) => {
  const [currentSlideNumber, setCurrentSlideNumber] = useState(1)
  const [zoomGalleryItems, setZoomGalleryItems] = useState()

  const slides = useMemo(() => {
    const parsedItems = JSON.parse(items || '[]')

    if (sketchfabId) {
      parsedItems.push({
        thumb: sketchfabThumb,
        sketchfabId: sketchfabId
      })
    }

    return parsedItems
  }, [items, sketchfabId, sketchfabThumb])

  const currentSlideIndex = useMemo(() => currentSlideNumber - 1, [
    currentSlideNumber
  ])

  const sketchfabTrackingEvent = useMemo(
    () => ({
      ...TRACKING_EVENTS.sketchfabClick,
      eventLabel: `imagem_3D-${carouselTrackingValue}`
    }),
    [carouselTrackingValue]
  )

  const navigationTrackingEvent = useMemo(
    () => ({
      prev: {
        ...TRACKING_EVENTS.prevArrowClick,
        eventValue: carouselTrackingValue
      },
      next: {
        ...TRACKING_EVENTS.nextArrowClick,
        eventValue: carouselTrackingValue
      }
    }),
    [carouselTrackingValue]
  )

  const buildZoomGalleryItems = useCallback(() => {
    const zoomGallery = slides.reduce((totalItems, item) => {
      if (!item.youtubeId && !item.sketchfabId) {
        totalItems.push({
          src: item.zoomUrl,
          title: item.alt,
          w: item.zoomDimensions,
          h: item.zoomDimensions
        })
      }

      return totalItems
    }, [])

    setZoomGalleryItems(zoomGallery)
  }, [setZoomGalleryItems, slides])

  const onImageClick = index => () => {
    const options = {
      ...ZOOM_OPTIONS.options,
      index
    }

    const plugin = new PhotoSwipe(
      document.querySelector(ZOOM_OPTIONS.container),
      PhotoSwipeUIDefault,
      zoomGalleryItems,
      options
    )

    plugin.init()
  }

  const onSlideChange = useCallback(({ realIndex }) => {
    setCurrentSlideNumber(realIndex + 1)
  }, [])

  const renderItem = (item, index) => {
    if (item.sketchfabId) {
      return (
        <SketchfabModel
          key={item.sketchfabId}
          modelId={item.sketchfabId}
          placeholder={sketchfabPlaceholder}
          trackingEvent={sketchfabTrackingEvent}
        />
      )
    }

    if (item.youtubeId) {
      return (
        <YoutubePlayer
          key={item.youtubeId}
          videoId={item.youtubeId}
          previewSrc={item.url}
          previewAlt={item.alt}
          isCurrentSlide={index === currentSlideIndex}
        />
      )
    }

    return (
      <S.ImageWrapper
        key={index}
        onClick={onImageClick(index)}
        eventValue={carouselTrackingValue}
        {...TRACKING_EVENTS.zoomClick}
      >
        <img src={item.url} alt={item.alt} />
      </S.ImageWrapper>
    )
  }

  const renderNavigation = useCallback(
    ({ prevProps, nextProps }) => (
      <Navigation
        prevProps={prevProps}
        nextProps={nextProps}
        prevArrowTrackingEvent={navigationTrackingEvent.prev}
        nextArrowTrackingEvent={navigationTrackingEvent.next}
      />
    ),
    [navigationTrackingEvent]
  )

  const isCurrentSlideInteractive = () => {
    return (
      slides[currentSlideIndex].sketchfabId ||
      slides[currentSlideIndex].youtubeId
    )
  }

  useEffect(() => {
    buildZoomGalleryItems()
  }, [buildZoomGalleryItems])

  if (!slides.length) {
    return null
  }

  return (
    <S.Wrapper>
      {!isCurrentSlideInteractive() && (
        <S.Counter>
          <Badge>
            {currentSlideNumber}/{slides.length}
          </Badge>
        </S.Counter>
      )}

      <GenericCarousel
        autoplay={{ delay: 5000 }}
        loop={slides.length > 1 && !sketchfabId}
        onSlideChange={onSlideChange}
        pagination={{ clickable: true, dynamicBullets: true }}
        customNavigation={renderNavigation}
        slidesPerView={1}
        hasOffset={false}
        freeMode={false}
      >
        {slides.map(renderItem)}
      </GenericCarousel>
    </S.Wrapper>
  )
}

export default memo(ImagesCarousel)
